/**
 * Quais ações fazem sentido pra cada stage do funil.
 *
 * Antes da PR-M1-01c-iii.b, a página de detalhe do lead mostrava sempre
 * "Reenviar questionário", "Criar proposta", "Marcar como perdido" e
 * "Excluir lead" — mesmo quando o lead já tinha virado cliente
 * (signed/provisioned). Karla podia clicar errado e mandar questionário
 * pra cliente ativo. Reportado pelo Alexandre 2026-04-25.
 *
 * Helper centraliza a lógica state machine: dado o stage atual, retorna
 * 3 buckets de ações:
 *
 *   - **primary**: ação principal contextual — o que Karla mais
 *     provavelmente quer fazer agora.
 *   - **secondary**: ações de apoio (editar dados, ver linha do tempo)
 *     que continuam fazendo sentido em qualquer estado.
 *   - **destructive**: ações destrutivas (excluir, reverter pra perdido)
 *     que devem ficar em overflow `⋯ Mais opções` pra reduzir clique
 *     acidental, com confirmação obrigatória.
 *
 * Componentes consomem o helper e renderizam cada bucket conforme o
 * design (botão primário, outline, dropdown).
 */

import type { LeadStage } from './schemas.js';

export type LeadAction =
  | 'send_form' // disparar questionário (form template)
  | 'resend_form' // re-disparar questionário (lead ainda no funil)
  | 'edit_lead' // editar dados cadastrais
  | 'create_proposal' // criar nova proposta (rascunho ou final)
  | 'view_proposals' // ver propostas existentes (link/scroll)
  | 'generate_contract' // gerar contrato (proposta aceita)
  | 'view_contract' // abrir contrato (gerado, em revisão pelo lead)
  | 'view_signed_contract' // abrir PDF do contrato assinado
  | 'open_clinic_panel' // ir pro painel da clínica provisionada (M1-01g.a)
  | 'view_timeline' // scrollar/abrir timeline
  | 'mark_lost' // marcar como perdido
  | 'revert_to_lost' // estado terminal: caso extremo (cancelamento pré-provisão)
  | 'delete_lead'; // hard delete (LGPD ou erro)

export interface LeadStageActions {
  primary: readonly LeadAction[];
  secondary: readonly LeadAction[];
  destructive: readonly LeadAction[];
}

/** Estado terminal pré-cliente: assinado mas ainda não provisionado.
 * Hoje a transição signed → provisioned ainda é manual (webhook
 * Assinafy é débito). Quando provisioning entrar (M1-01g.a), automatiza. */
const SIGNED_ACTIONS: LeadStageActions = {
  primary: ['view_signed_contract'],
  secondary: ['view_timeline', 'edit_lead'],
  // 'mark_lost' some — lead já fechou. 'revert_to_lost' fica como
  // exceção em overflow pra cobrir cancelamento extra-judicial.
  destructive: ['revert_to_lost', 'delete_lead'],
};

/** Estado terminal pós-cliente: clínica provisionada, lead virou tenant.
 * Ações de funil somem totalmente — Karla agora atua pelo painel da
 * clínica (futuro M1-01g.a). */
const PROVISIONED_ACTIONS: LeadStageActions = {
  primary: ['open_clinic_panel', 'view_signed_contract'],
  secondary: ['view_timeline', 'edit_lead'],
  destructive: ['delete_lead'],
};

const LOST_ACTIONS: LeadStageActions = {
  // Lead perdido — Karla pode ainda querer recuperar (mover pra
  // proposal_sent novamente) ou só consultar. UX de "recuperar"
  // fica pro futuro; por ora foco no que existe.
  primary: [],
  secondary: ['view_timeline', 'edit_lead'],
  destructive: ['delete_lead'],
};

const DEFAULT_FUNNEL_ACTIONS: LeadStageActions = {
  // Estados ativos do funil (new, form_sent, form_answered,
  // proposal_sent, proposal_accepted, contract_sent): mantém o leque
  // tradicional. Page já tem botões dedicados (SendQuestionnaireButton,
  // NewProposalDialog, etc.) — esse helper só sinaliza quais existem.
  primary: ['send_form', 'create_proposal'],
  secondary: ['view_timeline', 'edit_lead'],
  destructive: ['mark_lost', 'delete_lead'],
};

/** Resolve o conjunto de ações pertinentes pra um stage. */
export function getStageActions(stage: LeadStage): LeadStageActions {
  switch (stage) {
    case 'signed':
      return SIGNED_ACTIONS;
    case 'provisioned':
      return PROVISIONED_ACTIONS;
    case 'lost':
      return LOST_ACTIONS;
    case 'new':
    case 'form_sent':
    case 'form_answered':
    case 'proposal_sent':
    case 'proposal_accepted':
    case 'contract_sent':
    default:
      return DEFAULT_FUNNEL_ACTIONS;
  }
}

/** Helper boolean — `true` se a ação está disponível em qualquer bucket
 * do stage. Útil pra renderização condicional simples nos componentes
 * (`{actions.has('mark_lost') ? <BotaoLost/> : null}`). */
export function hasStageAction(stage: LeadStage, action: LeadAction): boolean {
  const a = getStageActions(stage);
  return (
    a.primary.includes(action) || a.secondary.includes(action) || a.destructive.includes(action)
  );
}
